import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { debounce } from 'lodash';

import {
  LIST_CHATROOMS,
  UPDATE_CHATROOM_SETTINGS,
} from '../../../http/apollo-queries';
import { Icon, Loading } from '../../UI';
import { Modal } from '../../Modal';
import { DropdownMenu } from '../../form';
import { ChatContext, useChat } from '../../../context/Chat';
import WindowWidget from '../WindowWidget';
import ChatList from './ChatList';
import Chatroom from './ChatRoom';
import SettingsModal from './SettingsModal';
import MutedUsersModal from './MutedUsersModal';
import { ListChatRoomsResponse, ChatRoom, ActionTypes } from './types';
import { useAuth } from '../../../context/Auth';
import BlockedChat from './BlockedChat';
import { useSidebar } from '../../../context/Sidebar';

enum ModalTypes {
  SETTINGS = 'settings',
  MUTED = 'mutedUsers',
}

const modals: Record<ModalTypes, React.ComponentType<any>> = {
  settings: SettingsModal,
  mutedUsers: MutedUsersModal,
};

interface PropTypes {
  windowed?: boolean;
}

const Chat: React.FC<PropTypes> = ({ windowed }) => {
  const { user } = useAuth();
  const { chatId, setChatId, notificationMessage } = useChat();

  const { isOpen, setIsOpen } = useSidebar();

  const { t } = useTranslation();
  const [modalType, setModalType] = useState<keyof typeof modals | null>(null);
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [messageSearchQuery, setMessageSearchQuery] = useState<string>();

  const [listChatRooms, { loading: chatRoomsLoading }] =
    useLazyQuery<ListChatRoomsResponse>(LIST_CHATROOMS, {
      variables: {
        limit: 100,
      },
      onCompleted: (response) => {
        if (response) {
          setChatRooms(response.listChatRooms.items);
        }
      },
    });

  const [updateChatRoomSettings] = useMutation(UPDATE_CHATROOM_SETTINGS, {
    onCompleted: listChatRooms,
  });

  const chatRoom = chatRooms.find(({ id }) => chatId === id);
  const chatTitle = chatRoom?.title;

  const handleSettingsSave = useCallback(
    (variables: {
      chatRoomId: string;
      soundEnabled: boolean;
      notificationsEnabled: boolean;
    }) => {
      if (variables.chatRoomId) {
        updateChatRoomSettings({
          variables,
        }).then(() => setModalType(null));
      }
    },
    []
  );

  const modalProps = {
    [ModalTypes.SETTINGS]: {
      soundEnabled:
        chatRoom && (chatRoom.soundEnabled || chatRoom.soundEnabled == null),
      notificationsEnabled:
        chatRoom &&
        (chatRoom.notificationsEnabled ||
          chatRoom.notificationsEnabled == null),
      chatRoomId: chatId,
      onSave: handleSettingsSave,
    },
    [ModalTypes.MUTED]: {},
  };

  useEffect(() => {
    if (user?.profile?.id) {
      listChatRooms();
    }
  }, [user?.profile]);

  useEffect(() => {
    if (notificationMessage) {
      switch (notificationMessage.action) {
        case ActionTypes.CREATE:
          setChatRooms((prevState) => {
            const index = chatRooms.findIndex(
              ({ id }) => id === notificationMessage.chatRoomId
            );

            if (index !== -1) {
              const newState = [...prevState];

              newState[index] = {
                ...prevState[index],
                lastMessage: notificationMessage,
              };

              return newState;
            }
            return prevState;
          });

          break;
        case ActionTypes.DELETE:
          listChatRooms();
      }
    }
  }, [notificationMessage]);

  const ModalComp = modalType && modals[modalType];

  const openChatWindow = () => {
    const el = document.getElementById('chatroom-wrapper');
    window.open(
      window.origin + '/chat',
      'newwindow',
      `width=${el?.clientWidth},height=${el?.clientHeight}`
    );
    return false;
  };

  const handleChatRoomSearch = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      setMessageSearchQuery(e.target.value);
    }, 800),
    []
  );

  const isBlocked = user && user.chatStatus === 'BLOCKED';

  return isOpen || windowed ? (
    <WindowWidget
      title="chatroom"
      id="chatroom-wrapper"
      onChange={!isBlocked ? handleChatRoomSearch : undefined}
      windowed={windowed}
      actions={[
        ...(!isBlocked
          ? [
              <DropdownMenu
                key={1}
                dropdownItems={[
                  <span
                    key={1}
                    onClick={() => setModalType(ModalTypes.SETTINGS)}
                  >
                    {t('chatroom.settings')}
                  </span>,
                  <span key={2} onClick={() => setModalType(ModalTypes.MUTED)}>
                    {t('chatroom.muted')}
                  </span>,
                ]}
                dropdownClassName="self-center"
                dropdownTooltipClassName="bg-white border-solid border-grey-10 border shadow-md"
              >
                <Icon
                  key="settings"
                  svgName="settings"
                  className="cursor-pointer"
                />
              </DropdownMenu>,
              <Icon
                key={3}
                svgName={'resize'}
                onClick={openChatWindow}
                className={'cursor-pointer'}
              />,
            ]
          : []),
        <Icon
          key="close"
          svgName="close"
          className="h-3 cursor-pointer"
          onClick={() => setIsOpen(false)}
        />,
      ]}
    >
      {isBlocked ? (
        <BlockedChat />
      ) : chatId ? (
        <>
          {ModalComp && (
            <Modal
              onCloseClick={() => setModalType(null)}
              element={<ModalComp {...modalProps[modalType]} />}
              isOpen={!!modalType}
            />
          )}
          <Chatroom
            chatRoomId={chatId}
            title={chatTitle as string}
            messageSearchQuery={messageSearchQuery}
            setChatId={setChatId}
            notificationMessage={notificationMessage}
          />
        </>
      ) : chatRoomsLoading ? (
        <Loading className="pt-16 m-auto" status={true} />
      ) : (
        <ChatList setChatId={setChatId} chatRooms={chatRooms} />
      )}
    </WindowWidget>
  ) : null;
};

export default Chat;
